import { useCallback, useEffect, useState } from 'react';
import { CatalogProductStatus } from '../services/api';
import { isAddedFilterValue, isLifecycleFilterValue, isMatchFilterValue, LifecycleFilterValue, MatchFilterValue } from './productBrowserFilterState';

/**
 * Saved product-browser filter sets.
 *
 * Locked as per-user, localStorage-only for this pass (no backend doc) — see
 * the Product Browser Filters design handoff. The read/write surface here is
 * intentionally narrow (list, save, remove) so swapping the storage for a
 * real per-user backend document later only touches this file.
 */
export interface ProductBrowserSavedFilters {
  productType: 'all' | 'camera' | 'lens' | 'accessory';
  search: string;
  /** Admin/vendor: single brand. Store users select from `storeBrands` instead. */
  brand: string;
  storeBrands: string[];
  category: string;
  catalogStatus: CatalogProductStatus;
  syncStatus: 'all' | 'in_sync' | 'not_in_sync' | 'sync_issues';
  /** "Added" filter (see `addedFilterToSince`); missing in views saved before it existed. */
  added?: string;
  /** "Matched to my store" filter; missing in views saved before it existed. */
  match?: MatchFilterValue;
  /** Lifecycle filter (flag `preorder_lifecycle`); missing in views saved before it existed. */
  lifecycle?: LifecycleFilterValue;
  /** Which optional pills (category/sync) were added, and in what order. */
  pillOrder: string[];
}

export interface ProductBrowserSavedView {
  id: string;
  name: string;
  filters: ProductBrowserSavedFilters;
}

const STORAGE_PREFIX = 'productBrowser.savedViews';

const storageKey = (userId?: string | null): string => `${STORAGE_PREFIX}.${userId || 'anon'}`;

const PRODUCT_TYPES: ProductBrowserSavedFilters['productType'][] = ['all', 'camera', 'lens', 'accessory'];
const CATALOG_STATUSES: CatalogProductStatus[] = ['active', 'archived', 'all'];
const SYNC_STATUSES: ProductBrowserSavedFilters['syncStatus'][] = ['all', 'in_sync', 'not_in_sync', 'sync_issues'];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/**
 * Validates every field's allowed values, not just its JS type — a tampered
 * or stale localStorage entry (e.g. an old build's productType/catalogStatus
 * enum) must be rejected rather than fed into filtering/API calls at runtime.
 */
function isSavedFilters(value: unknown): value is ProductBrowserSavedFilters {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.productType === 'string' &&
    PRODUCT_TYPES.includes(v.productType as ProductBrowserSavedFilters['productType']) &&
    typeof v.search === 'string' &&
    typeof v.brand === 'string' &&
    isStringArray(v.storeBrands) &&
    typeof v.category === 'string' &&
    typeof v.catalogStatus === 'string' &&
    CATALOG_STATUSES.includes(v.catalogStatus as CatalogProductStatus) &&
    typeof v.syncStatus === 'string' &&
    SYNC_STATUSES.includes(v.syncStatus as ProductBrowserSavedFilters['syncStatus']) &&
    isStringArray(v.pillOrder) &&
    (v.added === undefined || isAddedFilterValue(v.added)) &&
    (v.match === undefined || isMatchFilterValue(v.match)) &&
    (v.lifecycle === undefined || isLifecycleFilterValue(v.lifecycle))
  );
}

const sameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const left = a.slice().sort();
  const right = b.slice().sort();
  return left.every((item, index) => item === right[index]);
};

/**
 * Whether two filter sets describe the same query.
 *
 * `storeBrands` and `pillOrder` are order-insensitive as far as results go —
 * picking Nikon then Sony queries exactly what picking Sony then Nikon does,
 * and pill order is only how the row is laid out. Comparing them by raw
 * JSON.stringify made those reorderings read as a mismatch, which un-lit a
 * saved view that still matched what was on screen.
 */
export function sameSavedFilters(
  a: ProductBrowserSavedFilters,
  b: ProductBrowserSavedFilters,
): boolean {
  return (
    a.productType === b.productType &&
    a.search === b.search &&
    a.brand === b.brand &&
    a.category === b.category &&
    a.catalogStatus === b.catalogStatus &&
    a.syncStatus === b.syncStatus &&
    (a.added ?? '') === (b.added ?? '') &&
    (a.match ?? 'all') === (b.match ?? 'all') &&
    (a.lifecycle ?? 'all') === (b.lifecycle ?? 'all') &&
    sameMembers(a.storeBrands, b.storeBrands) &&
    sameMembers(a.pillOrder, b.pillOrder)
  );
}

function readViews(userId?: string | null): ProductBrowserSavedView[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is ProductBrowserSavedView =>
        !!v && typeof v.id === 'string' && typeof v.name === 'string' && isSavedFilters(v.filters),
    );
  } catch {
    return [];
  }
}

function writeViews(userId: string | null | undefined, views: ProductBrowserSavedView[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(views));
  } catch {
    /* Private-mode / storage quota — saved views are a convenience, not state we own. */
  }
}

export function useSavedViews(userId?: string | null) {
  const [views, setViews] = useState<ProductBrowserSavedView[]>(() => readViews(userId));

  // Re-read when the signed-in user changes (impersonation, sign-out/in).
  useEffect(() => {
    setViews(readViews(userId));
  }, [userId]);

  const save = useCallback(
    (name: string, filters: ProductBrowserSavedFilters): ProductBrowserSavedView => {
      const view: ProductBrowserSavedView = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        filters,
      };
      setViews((prev) => {
        const next = [...prev.filter((v) => v.name !== name), view];
        writeViews(userId, next);
        return next;
      });
      return view;
    },
    [userId],
  );

  const remove = useCallback(
    (id: string) => {
      setViews((prev) => {
        const next = prev.filter((v) => v.id !== id);
        writeViews(userId, next);
        return next;
      });
    },
    [userId],
  );

  return { views, save, remove };
}
